import clsx from "clsx";
import React from "react";

export type SimpleTableRecord = {
  // 左側に表示されるラベル
  label: string | React.ReactNode;
  // 右側に表示される値
  value: string | React.ReactNode;
  // `onRowClick` が指定された場合にクリック時に渡されるキー
  key?: string;
  // 非表示にするかどうか
  hidden?: boolean;
  // ヘッダーとして表示するかどうか（太字にする）
  isHeader?: boolean;
};

/**
 * ラベルと値のペアを表示するシンプルなテーブルコンポーネント
 */
export function SimpleTable({
  data,
  onRowClick,
  withoutSeparator = false,
  fontSize = 12,
  labelAlign = "left",
  valueAlign = "right",
  labelEnhanced = false,
  valueEnhanced = false,
  className,
}: {
  /** テーブルのデータ */
  data: SimpleTableRecord[];
  /** 行クリック時のコールバック */
  onRowClick?: (key: string) => void;
  /** セパレーターを表示しないかどうか */
  withoutSeparator?: boolean;
  /** フォントサイズ */
  fontSize?: number;
  /** ラベルの位置 */
  labelAlign?: "left" | "right";
  /** 値の位置 */
  valueAlign?: "left" | "right";
  /** ラベルを強調表示するかどうか */
  labelEnhanced?: boolean;
  /** 値を強調表示するかどうか */
  valueEnhanced?: boolean;
  /** クラス名（スタイルを上書きする場合） */
  className?: string;
}) {
  function handleClick(key: string) {
    onRowClick?.(key);
  }

  const fontSizeClass = `text-[${fontSize}px]`;

  return (
    <table className={clsx("w-full", fontSizeClass, className)}>
      <tbody>
        {data
          .filter((record) => !record.hidden)
          .map((record, index) => {
            const label =
              typeof record.label === "string" ? (
                <span>{record.label}</span>
              ) : (
                record.label
              );
            const value =
              typeof record.value === "string" ? (
                <span>{record.value}</span>
              ) : (
                record.value
              );
            const key = record.key ?? String(index);
            const isHeader = record.isHeader ?? false;
            return (
              <tr
                // 汎用部品で呼び出し元ごとの id を付けられない。計装は呼び出し元の導線で行う
                data-analytics-ignore
                key={index}
                className={clsx(
                  withoutSeparator ? "h-[30px]" : "h-[40px]",
                  !withoutSeparator && "border-b-sbeige border-b",
                  onRowClick && "cursor-pointer hover:opacity-60",
                )}
                onClick={() => handleClick(key)}
              >
                <th
                  className={clsx(
                    "text-left",
                    isHeader || labelEnhanced ? "font-bold" : "font-normal",
                    labelAlign === "right" ? "text-right" : "text-left",
                    "whitespace-nowrap",
                    withoutSeparator ? "align-top" : "align-middle",
                  )}
                >
                  {label}
                </th>
                <td
                  className={clsx(
                    valueAlign === "left" ? "pl-2 text-left" : "text-right",
                    valueEnhanced ? "font-bold" : "font-normal",
                    withoutSeparator ? "align-top" : "align-middle",
                  )}
                >
                  {value}
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
  );
}
